import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import contactService, { ImportContactJob } from '../services/contactService';

interface InstantlyAiImportModalProps {
  open: boolean;
  onClose: () => void;
  clientId: number;
  onSuccess?: () => void;
}

const InstantlyAiImportModal: React.FC<InstantlyAiImportModalProps> = ({
  open,
  onClose,
  clientId,
  onSuccess,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [testMode, setTestMode] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [importJobId, setImportJobId] = useState<string | null>(null);
  const [progress, setProgress] = useState<ImportContactJob | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [isDragging, setIsDragging] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setFile(null);
      setTestMode(false);
      setUploading(false);
      setError(null);
      setSuccess(false);
      setImportJobId(null);
      setProgress(null);
      setAutoRefresh(true);
      setRefreshInterval(5);
    } else {
      // Clean up interval when modal closes
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    // Set up polling when we have a job_id
    if (importJobId && autoRefresh && open) {
      // Clear existing interval
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Set up new interval
      intervalRef.current = setInterval(() => {
        checkProgress();
      }, refreshInterval * 1000);

      // Check immediately
      checkProgress();

      // Cleanup on unmount or when dependencies change
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      // Clear interval if conditions aren't met
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [importJobId, autoRefresh, refreshInterval, open]);

  const checkProgress = async () => {
    if (!importJobId) return;

    try {
      const jobProgress = await contactService.checkImportProgress(importJobId, clientId);
      setProgress(jobProgress);

      // If job is completed or failed, stop polling
      if (jobProgress.status === 'completed' || jobProgress.status === 'failed' || jobProgress.status === 'stopped') {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (jobProgress.status === 'completed') {
          setSuccess(true);
          // Call onSuccess callback to refresh jobs list in parent
          if (onSuccess) {
            setTimeout(() => onSuccess(), 500); // Small delay to ensure backend has committed
          }
        }
      }
    } catch (err: any) {
      console.error('Error checking progress:', err);
      // Don't set error here, just log it - we'll keep polling
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setError(null);
      setSuccess(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      // Check if it's a CSV file
      if (droppedFile.name.toLowerCase().endsWith('.csv')) {
        setFile(droppedFile);
        setError(null);
        setSuccess(false);
      } else {
        setError('Please drop a CSV file');
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(false);
      setProgress(null);

      const result = await contactService.importInstantlyAiCsv(file, clientId, testMode);

      if (testMode && result.test_result) {
        // Test mode - show result immediately
        setSuccess(true);
        setProgress({
          id: 0,
          client_id: clientId,
          filename: file.name,
          tag_name: result.tag_name,
          tag_id: result.tag_id,
          status: 'completed',
          total_rows: 1,
          processed_rows: 1,
          created_count: result.test_result.was_created ? 1 : 0,
          updated_count: result.test_result.was_updated ? 1 : 0,
          skipped_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        } as ImportContactJob);
      } else if (result.job_id) {
        // Full import - start polling
        setImportJobId(result.job_id);
        console.log('Import job started with ID:', result.job_id);
      } else {
        setError('No job ID returned from server');
      }
    } catch (err: any) {
      console.error('Error importing CSV:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to import CSV');
    } finally {
      setUploading(false);
    }
  };

  const handleManualRefresh = () => {
    checkProgress();
  };

  const handleClose = () => {
    // Clear interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Load Instantly.ai Contacts</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* File Selection with Drag and Drop */}
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{
              border: '2px dashed',
              borderColor: isDragging ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: isDragging ? 'action.hover' : 'transparent',
              transition: 'all 0.2s ease-in-out',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'action.hover',
              },
            }}
          >
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="csv-file-input" style={{ cursor: 'pointer', width: '100%', display: 'block' }}>
              {file ? (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {file.name}
                  </Typography>
                  <Button variant="outlined" size="small" component="span">
                    Change File
                  </Button>
                </Box>
              ) : (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {isDragging ? 'Drop CSV file here' : 'Drag and drop CSV file here or click to select'}
                  </Typography>
                  <Button variant="outlined" component="span">
                    Select CSV File
                  </Button>
                </Box>
              )}
            </label>
          </Box>

          {/* Test Mode Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
                disabled={uploading || !!importJobId}
              />
            }
            label="Test Mode (load only first contact)"
          />

          {/* Error Display */}
          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          {/* Success Display */}
          {success && progress && (
            <Alert severity="success">
              {testMode ? 'Test import completed!' : 'Import completed successfully!'}
              {progress.tag_name && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Tag: {progress.tag_name} {progress.tag_id && `(ID: ${progress.tag_id})`}
                </Typography>
              )}
            </Alert>
          )}

          {/* Progress Display */}
          {importJobId && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2">Import Progress</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {autoRefresh && (
                    <Typography variant="caption" color="text.secondary">
                      Auto-refresh: {refreshInterval}s
                    </Typography>
                  )}
                  <IconButton size="small" onClick={handleManualRefresh}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>

              {!progress ? (
                <Alert severity="info">
                  Starting import... Waiting for progress updates...
                </Alert>
              ) : (
                <Box>
                  <LinearProgress
                    variant="determinate"
                    value={progress.total_rows > 0 ? (progress.processed_rows / progress.total_rows) * 100 : 0}
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2">
                    {progress.processed_rows} / {progress.total_rows} rows processed ({Math.round((progress.processed_rows / progress.total_rows) * 100)}%)
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Created: {progress.created_count} | Updated: {progress.updated_count} | Skipped: {progress.skipped_count}
                  </Typography>
                  {progress.error_message && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {progress.error_message}
                    </Alert>
                  )}
                </Box>
              )}

              {/* Auto-refresh Controls */}
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                    />
                  }
                  label="Auto-refresh"
                />
                {autoRefresh && (
                  <TextField
                    type="number"
                    label="Interval (seconds)"
                    value={refreshInterval}
                    onChange={(e) => {
                      const val = parseInt(e.target.value, 10);
                      if (val >= 1 && val <= 300) {
                        setRefreshInterval(val);
                      }
                    }}
                    inputProps={{ min: 1, max: 300 }}
                    size="small"
                    sx={{ width: 120 }}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Test Result Display */}
          {testMode && success && progress && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Test Contact Result:
              </Typography>
              <Box
                component="pre"
                sx={{
                  p: 2,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.875rem',
                }}
              >
                {JSON.stringify(progress, null, 2)}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading && !importJobId}>
          {importJobId ? 'Close' : 'Cancel'}
        </Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={!file || uploading || (!!importJobId && progress?.status !== 'completed' && progress?.status !== 'failed' && progress?.status !== 'stopped')}
        >
          {uploading ? 'Uploading...' : testMode ? 'Test Import' : 'Start Import'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InstantlyAiImportModal;






